import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Switch,
  useToast,
} from '@ais-ui/ui-core';
import * as z from 'zod';
import { useSettings } from '../../SettingsProvider';
import { useSaveSettings } from '@ais-ui/websockets';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@ais-ui/utils';
import React, { useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettingsPage } from '../../SettingsPageProvider';

const labelFormSchema = z.object({
  assignedMode: z.boolean(),
  atonType: z.boolean(),
  country: z.boolean(),
  latitude: z.boolean(),
  longitude: z.boolean(),
  length: z.boolean(),
  mmsi: z.boolean(),
  offPosition: z.boolean(),
  state: z.boolean(),
  status: z.boolean(),
  targetType: z.boolean(),
  width: z.boolean(),
});

type LabelFormValues = z.infer<typeof labelFormSchema>;

export const AtonHoverLabelForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    assignedMode:
      settings['ribbonPlugin.settings']?.show_aton_assigned_mode || false,
    atonType: settings['ribbonPlugin.settings']?.show_aton_type || false,
    country: settings['ribbonPlugin.settings']?.show_aton_country || false,
    latitude: settings['ribbonPlugin.settings']?.show_aton_latitude || false,
    longitude: settings['ribbonPlugin.settings']?.show_aton_longitude || false,
    length: settings['ribbonPlugin.settings']?.show_aton_length || false,
    mmsi: settings['ribbonPlugin.settings']?.show_aton_mmsi || false,
    offPosition:
      settings['ribbonPlugin.settings']?.show_aton_off_position || false,
    state: settings['ribbonPlugin.settings']?.show_aton_state || false,
    status: settings['ribbonPlugin.settings']?.show_aton_status || false,
    targetType:
      settings['ribbonPlugin.settings']?.show_aton_target_type || false,
    width: settings['ribbonPlugin.settings']?.show_aton_width || false,
  };

  const form = useForm<LabelFormValues>({
    resolver: zodResolver(labelFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: LabelFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        show_aton_assigned_mode: data.assignedMode,
        show_aton_type: data.atonType,
        show_aton_country: data.country,
        show_aton_latitude: data.latitude,
        show_aton_longitude: data.longitude,
        show_aton_length: data.length,
        show_aton_mmsi: data.mmsi,
        show_aton_off_position: data.offPosition,
        show_aton_state: data.state,
        show_aton_status: data.status,
        show_aton_target_type: data.targetType,
        show_aton_width: data.width,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-aton-hover-settings-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsMapHoverSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-aton-hover-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsMapHoverFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                key="mmsi"
                control={form.control}
                name="mmsi"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapAtonHoverMmsiLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapAtonHoverMmsiDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-atonLabel-mmsi"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="country"
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapAtonHoverCountryLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapAtonHoverCountryDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-atonLabel-country"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="latitude"
                control={form.control}
                name="latitude"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapAtonHoverLatitudeModeLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapAtonHoverLatitudeDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-atonLabel-latitude"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="longitude"
                control={form.control}
                name="longitude"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapAtonHoverLongitudeLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapAtonHoverLongitudeDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-atonLabel-longitude"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="assignedMode"
                control={form.control}
                name="assignedMode"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapAtonHoverAssignedModeLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapAtonHoverAssignedModeDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-atonLabel-assignedMode"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="atonType"
                control={form.control}
                name="atonType"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapAtonHoverAtonTypeLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapAtonHoverAtonTypeDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-atonLabel-atonType"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="length"
                control={form.control}
                name="length"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapAtonHoverLengthLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapAtonHoverLengthDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-atonLabel-length"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="offPosition"
                control={form.control}
                name="offPosition"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapAtonHoverOffPositionModeLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapAtonHoverOffPositionDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-atonLabel-offPosition"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="state"
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapAtonHoverStateLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapAtonHoverStateDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-atonLabel-state"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="status"
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapAtonHoverStatusLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapAtonHoverStatusDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-atonLabel-status"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="width"
                control={form.control}
                name="width"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapAtonHoverWidthLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapAtonHoverWidthDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-atonLabel-width"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="targetType"
                control={form.control}
                name="targetType"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapAtonHoverTargetTypeLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapAtonHoverTargetTypeDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-atonLabel-targetType"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-atonLabels-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
